import {action, configure, makeObservable, observable} from "mobx";
import {inject, injectable} from "inversify";
import {PlanExtensionCatalogEntity} from "@dropDesk/domain/entities/plan_extensions/plan_extension_catalog.entity";
import {
    PlanExtensionSimulateHireEntity
} from "@dropDesk/domain/entities/plan_extensions/plan_extension_simulate_hire.entity";
import {CompanyExtensionEntity} from "@dropDesk/domain/entities/plan_extensions/company_extension.entity";
import {CatalogDisplayModeEnum} from "@dropDesk/domain/entities/plan_extensions/plan_extension.enum";
import {
    ListCatalogPlanExtensionsUseCase
} from "@dropDesk/domain/use_case/plan_extensions/list_catalog_plan_extensions.usecase";
import {
    GetCatalogByIdPlanExtensionUseCase
} from "@dropDesk/domain/use_case/plan_extensions/get_catalog_by_id_plan_extension.usecase";
import {
    SimulateHirePlanExtensionUseCase
} from "@dropDesk/domain/use_case/plan_extensions/simulate_hire_plan_extension.usecase";
import {HirePlanExtensionUseCase} from "@dropDesk/domain/use_case/plan_extensions/hire_plan_extension.usecase";
import {
    UnsubscribePlanExtensionUseCase
} from "@dropDesk/domain/use_case/plan_extensions/unsubscribe_plan_extension.usecase";
import {
    ReactivatePlanExtensionUseCase
} from "@dropDesk/domain/use_case/plan_extensions/reactivate_plan_extension.usecase";
import {
    ListInstalledPlanExtensionsUseCase
} from "@dropDesk/domain/use_case/plan_extensions/list_installed_plan_extensions.usecase";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import {translate} from "@dropDesk/storage/i18n/translate_helper";
import {PaymentEntity} from "@dropDesk/domain/entities/payment/payment.entity";

configure({
    enforceActions: "always",
});

@injectable()
export class ExtensionsController {

    private readonly _listCatalogUseCase: ListCatalogPlanExtensionsUseCase;
    private readonly _getCatalogByIdUseCase: GetCatalogByIdPlanExtensionUseCase;
    private readonly _simulateHireUseCase: SimulateHirePlanExtensionUseCase;
    private readonly _hireUseCase: HirePlanExtensionUseCase;
    private readonly _unsubscribeUseCase: UnsubscribePlanExtensionUseCase;
    private readonly _reactivateUseCase: ReactivatePlanExtensionUseCase;
    private readonly _listInstalledUseCase: ListInstalledPlanExtensionsUseCase;

    constructor(
        @inject(ListCatalogPlanExtensionsUseCase) listCatalogUseCase: ListCatalogPlanExtensionsUseCase,
        @inject(GetCatalogByIdPlanExtensionUseCase) getCatalogByIdUseCase: GetCatalogByIdPlanExtensionUseCase,
        @inject(SimulateHirePlanExtensionUseCase) simulateHireUseCase: SimulateHirePlanExtensionUseCase,
        @inject(HirePlanExtensionUseCase) hireUseCase: HirePlanExtensionUseCase,
        @inject(UnsubscribePlanExtensionUseCase) unsubscribeUseCase: UnsubscribePlanExtensionUseCase,
        @inject(ReactivatePlanExtensionUseCase) reactivateUseCase: ReactivatePlanExtensionUseCase,
        @inject(ListInstalledPlanExtensionsUseCase) listInstalledUseCase: ListInstalledPlanExtensionsUseCase,
    ) {
        makeObservable(this);
        this._listCatalogUseCase = listCatalogUseCase;
        this._getCatalogByIdUseCase = getCatalogByIdUseCase;
        this._simulateHireUseCase = simulateHireUseCase;
        this._hireUseCase = hireUseCase;
        this._unsubscribeUseCase = unsubscribeUseCase;
        this._reactivateUseCase = reactivateUseCase;
        this._listInstalledUseCase = listInstalledUseCase;
    }

    @observable
    loading = false;

    @observable
    loadingMessage?: string | null = null;

    @observable
    extensions: PlanExtensionCatalogEntity[] = [];

    @observable
    simulateHireResult?: PlanExtensionSimulateHireEntity | null = null;

    @observable
    selectedExtension?: PlanExtensionCatalogEntity | null = null;

    @observable
    installedExtensions: CompanyExtensionEntity[] = [];

    @action
    setLoadingMessage(message?: string | null) {
        this.loadingMessage = message;
    }

    @action
    startLoading(loadingMessage?: string | null) {
        this.setLoadingMessage(loadingMessage);
        this.loading = true;
    }

    @action
    stopLoading() {
        this.loading = false;
        this.setLoadingMessage(null);
    }

    private sortExtensions(extensions: PlanExtensionCatalogEntity[]): PlanExtensionCatalogEntity[] {
        const priority = (extension: PlanExtensionCatalogEntity): number => {
            const installed = extension.installed;
            if (extension.isCore || installed?.isFreeFromAccountMigrated) {
                return 2;
            }
            if (!!installed && !installed.isCanceled) {
                return 1;
            }
            return 0;
        };
        return [...extensions].sort((a, b) => priority(a) - priority(b));
    }

    @action
    setExtensions(extensions: PlanExtensionCatalogEntity[], mode: CatalogDisplayModeEnum = CatalogDisplayModeEnum.sorted) {
        if (mode === CatalogDisplayModeEnum.sorted) {
            this.extensions = this.sortExtensions(extensions);
        } else if (mode === CatalogDisplayModeEnum.installedOnly) {
            this.extensions = extensions.filter(e => !!e.installed && !e.installed.isCanceled);
        } else {
            this.extensions = extensions;
        }
    }

    @action
    setSimulateHireResult(result: PlanExtensionSimulateHireEntity | null) {
        this.simulateHireResult = result;
    }

    @action
    setSelectedExtension(extension: PlanExtensionCatalogEntity | null) {
        this.selectedExtension = extension;
    }

    @action
    setInstalledExtensions(extensions: CompanyExtensionEntity[]) {
        this.installedExtensions = [...extensions].sort((a, b) => {
            const isFreeA = a.isFree ? 1 : 0;
            const isFreeB = b.isFree ? 1 : 0;
            return isFreeA - isFreeB;
        });
    }

    @action
    async listCatalog(mode: CatalogDisplayModeEnum = CatalogDisplayModeEnum.sorted) {
        try {
            this.startLoading("extensions.listCatalog");
            const result = await this._listCatalogUseCase.call();
            this.setExtensions(result, mode);
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    async getCatalogById(id: string): Promise<PlanExtensionCatalogEntity | null> {
        try {
            this.startLoading("extensions.getCatalogById");
            const result = await this._getCatalogByIdUseCase.call(id);
            this.stopLoading();
            return result;
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
            return null;
        }
    }

    @action
    async simulateHire(idExtension: string) {
        try {
            this.startLoading("extensions.simulateHire");
            const result = await this._simulateHireUseCase.call(idExtension);
            this.setSimulateHireResult(result);
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    async hire(idExtension: string, onSuccessPayExtension: (newExtension: CompanyExtensionEntity, key: string) => void): Promise<CompanyExtensionEntity | null> {
        try {
            this.startLoading("extensions.hire");
            const newExtension = await this._hireUseCase.call(idExtension);
            onSuccessPayExtension(newExtension, "extensions.success.pay");
            this.stopLoading();
            return newExtension;
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
            return null;
        }
    }

    @action
    async unsubscribe(id: string, onSuccess?: () => Promise<void>) {
        try {
            this.startLoading("extensions.unsubscribe");
            await this._unsubscribeUseCase.call(id);
            await this.listCatalog();
            await onSuccess?.();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    async reactivate(id: string, onSuccess?: () => Promise<void>) {
        try {
            this.startLoading("extensions.reactivate");
            await this._reactivateUseCase.call(id);
            await this.listCatalog();
            await onSuccess?.();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    async listInstalled() {
        try {
            this.startLoading("extensions.listInstalled");
            const result = await this._listInstalledUseCase.call();
            this.setInstalledExtensions(result);
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    async initialize() {
        await this.listCatalog();
    }

    @action
    async initializeConfirmExtension(id: string) {
        try {
            this.startLoading("extensions.initializeConfirmExtension");
            const [extension, simulate] = await Promise.all([
                this._getCatalogByIdUseCase.call(id),
                this._simulateHireUseCase.call(id),
            ]);
            this.setSelectedExtension(extension);
            this.setSimulateHireResult(simulate);
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }
}
